/* Which salon this browser is currently the front desk for.
 *
 * The console keeps three things in localStorage that outlive a page load — the
 * durable write queue, the board cache, and the settings mirror — and every one
 * of them was a fixed string. On a single-salon install that was fine. On a
 * product it is a bug with teeth: a write queued at one salon and flushed after
 * signing in as another would land in the wrong database rows, and the settings
 * mirror would hand one salon's branding to the next station that opened the
 * page. So the keys are namespaced by tenant.
 *
 * ── Why this is a module of its own ─────────────────────────────────────────
 *
 * `prefs.js` reads its mirror synchronously at load, before the first paint and
 * long before there is a Supabase session — that is the whole point of the
 * mirror. It therefore cannot ask the server which tenant it is, and it
 * deliberately imports nothing but Vue so the settings rules stay testable
 * without standing a client up. `sync.js` has the same problem with the queue.
 *
 * Both need the same answer at the same impossible moment, so the answer is
 * cached here in a key that is NOT itself namespaced — it is what names the
 * namespace. It holds the last tenant this browser was signed in as, which is
 * the right guess often enough to boot from, and is corrected the moment the
 * session resolves.
 *
 * Nothing here imports anything. It is localStorage and string concatenation.
 */

var KEY = 'dreamspa.dash.tenant.v1';

function read() {
  try {
    var v = window.localStorage.getItem(KEY);
    return v || null;
  } catch (e) { return null; }
}

var current = read();

function get() { return current; }

/* The suffixed key for `base`.
 *
 * Signed out, or on the very first boot of a station that has never had a
 * session, this returns `base` unchanged — the un-namespaced key. That is
 * deliberate rather than a fallback: it is exactly the key every station is
 * already using today, so the first sign-in after the cutover finds the data
 * where it has always been and `adopt` moves it under the tenant. Nobody loses
 * their pending writes or their branding to a rename.
 */
function ns(base) { return current ? base + '.' + current : base; }

/* Move one namespaced value from `fromTenant`'s namespace into the current one.
 *
 * `fromTenant` is null when the value was written before this browser knew
 * about tenants at all, which is the interesting case: it is the migration off
 * the old fixed keys, and it happens once per station.
 *
 * A value already sitting at the destination wins. Two tabs signing in at once
 * would otherwise race, and the destination is the newer of the two by
 * definition.
 */
function move(base, fromTenant) {
  var src = fromTenant ? base + '.' + fromTenant : base;
  var dst = ns(base);
  if (src === dst) return;
  try {
    var v = window.localStorage.getItem(src);
    if (v != null && window.localStorage.getItem(dst) == null) {
      window.localStorage.setItem(dst, v);
    }
    window.localStorage.removeItem(src);
  } catch (e) { /* unavailable or full — the caller re-reads either way */ }
}

/* Record the tenant the session says this is. Returns the previous value if it
 * changed, so the caller knows which namespace to migrate from, or `false` if
 * nothing changed and there is nothing to move.
 *
 * A null id — signing out — deliberately does NOT clear `current`. The station
 * is still this salon's station; it just has nobody signed in at the moment,
 * and clearing it would push the queue back onto the un-namespaced key where
 * the next tenant to sign in would adopt it.
 */
function adopt(id) {
  if (!id || id === current) return false;
  var previous = current;
  current = id;
  try { window.localStorage.setItem(KEY, id); } catch (e) { /* full */ }
  return { previous: previous };
}

export const tenant = { get: get, ns: ns, move: move, adopt: adopt, KEY: KEY };
